// The app's ONE print template (L07): every list print funnels through
// rowsToPrintHtml / printRows. Conforms to Q44 — portrait (landscape only
// over 7 columns), the app font, a header of title + 'Printed <§5 date>' +
// optional filters line, 'Page N of M', statuses as words — and Table E
// item 23: date values (Date instances and ISO-prefix strings) render in the
// farm's Central time via exportDates, never as UTC timestamps.
//
// A print row is an opaque record: this module never knows which table it is
// rendering, only that a column names a key on the row or brings its own
// accessor. `unknown` values are safe because every one of them goes through
// printableText() before it reaches the HTML.
//
// Page numbers use @page margin-box counters (counter(page)/counter(pages)),
// which Firefox renders; Chromium does not support margin boxes and falls
// back to the browser's own print footer when the user enables it. A
// JavaScript pagination estimate would be dishonest, so there is none
// (Codex ruling C, 2026-10-16). The app font is declared as a stack
// ('Hanken Grotesk' then system fallbacks) WITHOUT a network font load: if
// the webfont is available to the print iframe it renders, otherwise the
// system fallback does — the printout stays readable either way (Codex
// ruling L2, 2026-10-16).
import {exportDateShort} from './exportDates.js';

export type PrintRow = Record<string, unknown>;

export type PrintColumn<Row extends PrintRow = PrintRow> = {
  header?: string;
  key?: string;
  value?: (row: Row) => unknown;
};

export type PrintRowsOptions<Row extends PrintRow = PrintRow> = {
  title?: string;
  subtitle?: string;
  /** One line under the printed date naming the active filters, when any. */
  filters?: string;
  columns?: PrintColumn<Row>[] | null;
  rows?: Row[] | null;
};

function printableText(value: unknown): string {
  if (value == null) return '';
  // Q44 / item 23: dates render as §5 short dates in Central time, never as
  // UTC timestamps. Detection is strict (see exportDates); everything else
  // keeps its plain-text rendering.
  const date = exportDateShort(value);
  if (date !== null) return date;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function escapeHtml(value: unknown): string {
  return printableText(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function columnValue<Row extends PrintRow>(column: PrintColumn<Row>, row: Row): unknown {
  if (typeof column.value === 'function') return column.value(row);
  return row && column.key ? row[column.key] : '';
}

export function rowsToPrintHtml<Row extends PrintRow>({
  title,
  subtitle = '',
  filters = '',
  columns,
  rows,
}: PrintRowsOptions<Row>): string {
  const cols = Array.isArray(columns) ? columns : [];
  const list = Array.isArray(rows) ? rows : [];
  // Q44: portrait by default; landscape only when the table is wide (over
  // seven columns). The @bottom-right margin box renders 'Page N of M' where
  // the engine supports margin boxes (Firefox); Chromium ignores it.
  const pageRule =
    cols.length > 7
      ? '@page{size:landscape;margin:12mm;@bottom-right{content:"Page " counter(page) " of " counter(pages)}}'
      : '@page{size:portrait;margin:14mm;@bottom-right{content:"Page " counter(page) " of " counter(pages)}}';
  const header = cols.map((c) => `<th>${escapeHtml(c.header || c.key || '')}</th>`).join('');
  const body = list
    .map((row) => {
      const cells = cols.map((c) => `<td>${escapeHtml(columnValue(c, row))}</td>`).join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');
  const printedLine = 'Printed ' + exportDateShort(new Date());
  const filterLine = filters ? `<div class="filters">Filters: ${escapeHtml(filters)}</div>` : '';

  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title || 'Print')}</title>
<style>
${pageRule}
body{font-family:'Hanken Grotesk','Segoe UI',system-ui,sans-serif;color:#1A1F24;margin:24px}
h1{font-size:20px;margin:0 0 4px}
.printed,.subtitle,.filters{font-size:12px;color:#5B636D;margin:0 0 4px}
.subtitle{margin-bottom:${filters ? '4px' : '16px'}}
.filters{margin-bottom:16px}
table{width:100%;border-collapse:collapse;font-size:11px}
thead{display:table-header-group}
th,td{border:1px solid #D2D6DB;padding:5px 6px;text-align:left;vertical-align:top}
th{background:#F1F3F4;font-weight:700}
tr{break-inside:avoid}
</style>
</head>
<body>
<h1>${escapeHtml(title || 'Print')}</h1>
<div class="printed">${escapeHtml(printedLine)}</div>
${filterLine}
${subtitle ? `<div class="subtitle">${escapeHtml(subtitle)}</div>` : ''}
<table>
<thead><tr>${header}</tr></thead>
<tbody>${body}</tbody>
</table>
</body>
</html>`;
}

export function printRows<Row extends PrintRow>(options: PrintRowsOptions<Row>): boolean {
  const {title, subtitle = '', filters = '', columns, rows} = options;
  if (
    typeof document === 'undefined' ||
    typeof window === 'undefined' ||
    typeof window.print !== 'function' ||
    !document.body
  ) {
    return false;
  }

  const frame = document.createElement('iframe');
  frame.setAttribute('data-print-export-frame', '1');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  document.body.appendChild(frame);

  const doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document);
  if (!doc) {
    frame.remove();
    return false;
  }

  const cleanup = () => {
    setTimeout(() => frame.remove(), 0);
  };
  const runPrint = () => {
    const targetWindow = frame.contentWindow || window;
    if (targetWindow && typeof targetWindow.focus === 'function') targetWindow.focus();
    if (targetWindow && typeof targetWindow.print === 'function') targetWindow.print();
    else window.print();
    cleanup();
  };

  doc.open();
  doc.write(rowsToPrintHtml({title, subtitle, filters, columns, rows}));
  doc.close();

  if (doc.readyState === 'complete') runPrint();
  else frame.onload = runPrint;
  return true;
}
